import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  CardMedia,
  CircularProgress,
  Avatar,
  Button,
  Slider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  Stack,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Breadcrumbs,
  Link,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { PlayArrow, Pause, FastForward, FastRewind, FileDownload } from "@mui/icons-material";
//import { PlayCircleFilled } from "@mui/icons-material"; // Import PlayCircleFilled icon//
import { getCamerasList, getClientGroupInfo } from "src/api/liveCameras";
import { getClientNow } from "src/utils/clientTime";
import dayjs from "dayjs";
import { unstable_batchedUpdates } from "react-dom";
//import { getvideoURLMail } from "src/api/clientDetails";
import {
  getClientEscalatedVideos,
  getEscalatedSummary,
} from "src/api/clientDetails";
import {  getClientCameras } from 'src/api/clientDetails';
//import FolderIcon from "@mui/icons-material/Folder";
import { styled } from "@mui/system";
import Iconify from 'src/components/Iconify';
//import { green } from '@mui/material/colors';
import Page from '../components/Page';
import { getClientLocations, getClientProperties } from 'src/api/ShiftMaster';
import { jsonToParam } from 'src/utils/config';
import { getAppStorageItem } from 'src/utils/storageProxy';
//import TestVideoPlayer from './TestVideoPlayer';
import PlaybackVideoPlayer from 'src/pages/PlaybackVideoPlayer';
// const timelineData = [
//   { start: 0, end: 420, type: "Normal" }, // 00:00 - 07:00 (blue)
//   { start: 420, end: 480, type: "Event" }, // 07:00 - 08:00 (red)
//   { start: 480, end: 600, type: "Normal" }, // 08:00 - 10:00 (blue)
//   { start: 600, end: 660, type: "Event" }, // 10:00 - 11:00 (red)
//   { start: 660, end: 1440, type: "Normal" }, // 11:00 - 24:00 (blue)
// ];
// Windowed loading: the player opens on the first hour of the day and lazy-loads
// the next hour as playback advances, instead of pulling the whole 24h. A time
// picked on the timeline fetches ~1hr (15 min before → 45 min after) around it.
const WINDOW_BEFORE_MIN = 15;
const WINDOW_AFTER_MIN = 45;
const NEXT_WINDOW_HOURS = 1;

// Stable identity for a clip, used to de-duplicate across overlapping fetches.
const clipKey = (item) =>
  item?.local_play_url || item?.play_url || item?.VideoUrl || item?.recording_start_time || "";

// Derive a clip's [start, end] window from its recording times or play URL params.
const getClipTimeRange = (item) => {
  let segStart = item?.recording_start_time ? dayjs(item.recording_start_time) : null;
  let segEnd = item?.recording_end_time ? dayjs(item.recording_end_time) : null;
  if (!segStart?.isValid() || !segEnd?.isValid()) {
    const url = item?.local_play_url || item?.play_url || item?.VideoUrl;
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const fromTs = Number(parsed.searchParams.get("from_ts"));
      const rawDur = Number(parsed.searchParams.get("duration"));
      if (!Number.isFinite(fromTs) || fromTs <= 0) return null;
      segStart = dayjs.unix(fromTs);
      segEnd = segStart.add(Number.isFinite(rawDur) && rawDur > 0 ? rawDur : 900, "second");
    } catch (_e) {
      return null;
    }
  }
  return { segStart, segEnd };
};

// Pick the clip that contains `target`; if none, the nearest recording at/after it,
// otherwise the last recording before it. Falls back to the first clip.
const findClipIndexForTime = (clips, target) => {
  if (!Array.isArray(clips) || clips.length === 0 || !target) return 0;

  for (let i = 0; i < clips.length; i++) {
    const range = getClipTimeRange(clips[i]);
    if (range && !target.isBefore(range.segStart) && !target.isAfter(range.segEnd)) return i;
  }

  let bestAfter = -1;
  let bestAfterStart = null;
  let lastBefore = -1;
  for (let i = 0; i < clips.length; i++) {
    const range = getClipTimeRange(clips[i]);
    if (!range) continue;
    if (!range.segStart.isBefore(target)) {
      if (bestAfterStart === null || range.segStart.isBefore(bestAfterStart)) {
        bestAfterStart = range.segStart;
        bestAfter = i;
      }
    } else {
      lastBefore = i;
    }
  }
  if (bestAfter !== -1) return bestAfter;
  if (lastBefore !== -1) return lastBefore;
  return 0;
};

const marks = [];
for (let h = 0; h < 24; h++) {
  const base = h * 60;
  marks.push({ value: base, label: `${h}` }); // Main hour
  marks.push({ value: base + 15, }); // Quarter hours
  marks.push({ value: base + 30 });
  marks.push({ value: base + 45 });
}

const DATE_TIMELINE_DAYS = 30;
const DATE_TIMELINE_VISIBLE_DAYS = 15;
const DATE_TIMELINE_OFFSET = DATE_TIMELINE_DAYS - 1;
const DATE_BOX_WIDTH = 45;
const DATE_ROW_GAP = 6;
const DATE_ROW_VISIBLE_WIDTH =
  (DATE_TIMELINE_VISIBLE_DAYS * DATE_BOX_WIDTH) + ((DATE_TIMELINE_VISIBLE_DAYS - 1) * DATE_ROW_GAP);

const DatePickerWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});

const NavButtonContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '10px',
  width: '100%',
});

const NavButton = styled(Button)(({ theme }) => ({
  minWidth: '26px',
  height: '56px',
  fontSize: '16px',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: 0,
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  border: 'none',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.03)',
  },
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
    cursor: 'not-allowed',
  },
}));

const DateRow = styled(Box)({
  display: 'flex',
  gap: `${DATE_ROW_GAP}px`,
  flex: '0 1 auto',
  width: `${DATE_ROW_VISIBLE_WIDTH}px`,
  maxWidth: 'calc(100% - 72px)',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  padding: '4px 0',
});

const DateText = styled(Typography)({
  fontWeight: "600",
  marginBottom: "8px",
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const getAssignedLocationsFromStorage = () => {
  try {
    const assignedLocationsRaw = getAppStorageItem('assigned_locations');
    if (!assignedLocationsRaw) return [];

    const parsedAssignedLocations = JSON.parse(assignedLocationsRaw);
    const assignedLocations = Array.isArray(parsedAssignedLocations)
      ? parsedAssignedLocations
      : [];

    return assignedLocations
      .map((locationItem) => ({
        ID: Number(locationItem?.ID ?? locationItem?.id ?? 0),
        Location: String(locationItem?.Location || locationItem?.name || '').trim(),
      }))
      .filter((locationItem) => locationItem.ID > 0);
  } catch (error) {
    console.error('Failed to read assigned_locations from storage:', error);
    return [];
  }
};

// const NavButton = styled(Button)({
//   padding: "8px",
//   fontSize: "24px",
//   fontWeight: "bold",
//   background: "none",
//   border: "none",
//   cursor: "pointer",
//   minWidth: "auto",
// });


// const SelectedDateText = styled(Typography)({
//   marginTop: "16px",
//   fontSize: "16px",
//   fontWeight: "500",
// });

// const DateBox1 = styled(Box, {
//   shouldForwardProp: (prop) => prop !== "isSelected", // Prevent isSelected from being passed to the DOM
// })(({ isSelected }) => ({
//   border: "1px solid #ccc",
//   borderRadius: "8px",
//   padding: "16px",
//   textAlign: "center",
//   width: "150px",
//   boxSizing: "border-box",
//   cursor: "pointer",
//   backgroundColor: isSelected ? "#031D49" : "#fff",
//   color: isSelected ? "#fff" : "#000",
// }));



const DateBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ isSelected, theme }) => ({
  border: isSelected
    ? `2px solid ${theme.palette.primary.light}`
    : `1px solid ${theme.palette.divider}`,
  borderRadius: '10px',
  padding: '5px 3px',
  textAlign: 'center',
  flex: `0 0 ${DATE_BOX_WIDTH}px`,
  minWidth: `${DATE_BOX_WIDTH}px`,
  height: '70px',
  boxSizing: 'border-box',
  cursor: 'pointer',
  backgroundColor: isSelected ? theme.palette.primary.main : theme.palette.background.paper,
  color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  fontSize: '0.65rem',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: isSelected
    ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
    : `0 1px 3px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.08)}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const MonthText = styled('div')({
  fontSize: '9px',
  fontWeight: 600,
  opacity: 0.85,
});

const DayNumber = styled('div')({
  fontSize: '14px',
  fontWeight: 'bold',
  margin: '2px 0',
});

const DayText = styled('div')({
  fontSize: '9px',
  // color: '#718096',
});


const VideoArchives = () => {
  const theme = useTheme();

  // const [startDate, setStartDate] = useState(() => {
  //   const initialStart = new Date(today);
  //   initialStart.setDate(today.getDate() - DATE_TIMELINE_OFFSET);
  //   return initialStart;
  // });
  const [value, setValue] = React.useState(0);

  //const [selectedCameras, setSelectedCameras] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogImage, setOpenDialogImage] = useState(false);
  const [currentCameraId, setCurrentCameraId] = useState(null);

  // const [clientCameras,setClientCameras] = useState([]);
  const [escalatedVideos, setEscalatedVideos] = useState([]);
  const [imageURL, setImageURL] = useState([]);

  //const [isValidFrom, setIsValidFrom] = useState(false);
  const [email, setEmail] = useState("");
  //const [url, setUrl] = useState("");
  const [selectedSpeed, setSelectedSpeed] = useState(3); // Initial playback speed
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDeviceVideos, setCurrentDeviceVideos] = useState([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const [clientGroupList, setClientGroupsList] = useState([]);
  const [camerasByGroupId, setCamerasByGroup] = useState([]);
  const [allCamerawithoutGroups, setAllCameraListWithoutGroups] = useState([]);
  const [camerawithoutGroups, setCameraListWithoutGroups] = useState([]);
  const [archivesCamerasList, setArchivesCamerasList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorFromdate, setFromDateErrorMessage] = useState("");
  const [errorTodate, setToErrorMessage] = useState("");
  const [initialToTime, setInitialToTime] = useState(true);
  const [checkValue, setCheckValue] = useState(false);
  const [activeCameras, setActiveCameras] = useState([]);
  const [inputValue, setInputValue] = useState([]);
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isTabledata, setIsTabledata] = useState(false);
  const [errorEmail, setErrorEmail] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [localURL, setLocalURL] = useState(false);
  const [calculatedDay, setCalculatedDay] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [groupinfo, setGroupinfo] = useState([]);
  const [errorGroup, setErrorGroup] = useState(null);
  const [groupCheckValue, setGroupCheckValue] = useState(false);
  const [activeGroups, setActiveGroups] = useState([]);
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const [escalatedSummary, setEscalatedSummary] = useState([]);
  const [seekTargetTime, setSeekTargetTime] = useState(null);
  // The calendar day the player timeline is anchored to. Kept separate from the
  // clip's own start day so a first clip that spans midnight (footage from the
  // previous day) doesn't make the ruler show the previous day.
  const [playerDay, setPlayerDay] = useState(null);
  // The contiguous time span currently fetched into the player ({ start, end }
  // dayjs). Lets the timeline tell "no recording" (a gap inside this span, shown
  // red) apart from "not loaded yet" (outside it), and drives click-to-load.
  const [loadedRange, setLoadedRange] = useState(null);

  // Windowed lazy-loading state for the player.
  const loadedWindowEndRef = useRef(null);      // end of the time range currently loaded
  const loadedKeysRef = useRef(new Set());      // clip keys already loaded (dedup)
  const prefetchingRef = useRef(false);         // guard: one window fetch in flight
  const pendingAutoAdvanceRef = useRef(false);  // advance to the next clip once loaded
  const hasMoreRef = useRef(false);             // more recordings may exist ahead
  const deviceVideosRef = useRef([]);           // mirror of escalatedSummary length/order
  const fetchSeqRef = useRef(0);                // latest-wins guard: ignore stale window fetches
  const dateChangeTimerRef = useRef(null);      // debounce rapid time selections into one fetch
  const propertyRequestRef = useRef(0);         // latest-wins guard for property camera requests
  const [timeSlots, setTimeSlots] = useState(new Set());
  const [eventSlots, setEventSlots] = useState(new Set())
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [downloading, setDownloading] = useState(false)
  const slowMotionSpeeds = [0.25, 0.5, 0.75];
  const fastForwardSpeeds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const [downloadText, setDownloadText] = useState(false)
  const [xsCount, setXSCountVal] = useState(null);
  const isInSlow = slowMotionSpeeds.includes(playbackRate);
  const isInFast = fastForwardSpeeds.includes(playbackRate);
  const [outerBoxWidth, setOuterBoxWidth] = useState('');
  const [cameWidth, setCamewidth] = useState('');
  const storedProperty = localStorage.getItem('Location');
  const AssignClient = localStorage.getItem('AssignClient');
  const userType = localStorage.getItem('user_type');
  const Type = localStorage.getItem('Type');

  const [location, setLocation] = useState(() => {
    const stored = localStorage.getItem('selectedLocation');
    return stored ? parseInt(stored) : null;
  });
  const [locations, setLocations] = useState([]);

  const [property, setProperty] = useState(() => {
    const stored = localStorage.getItem('selectedProperty');
    return stored ? parseInt(stored) : null;
  });
  const [properties, setProperties] = useState([]);

  let client_id = 0;
  if(localStorage.getItem('Type') === 'merge'){
    client_id = localStorage.getItem('ID');
  }
  else{
    client_id = localStorage.getItem('client_id');
  }


  const [watchedClips, setWatchedClips] = useState(new Set());


  const [tokenName, setToken] = useState(localStorage.getItem("displayname"));

  const [startDate, setStartDate] = useState(() => {
    if (!calculatedDay) return new Date();
    const initialStart = calculatedDay.toDate();
    initialStart.setDate(initialStart.getDate() - (DATE_TIMELINE_VISIBLE_DAYS - 1));
    return initialStart;
  });

  useEffect(() => {
    if (calculatedDay) {
      const initialStart = calculatedDay.toDate();
      initialStart.setDate(initialStart.getDate() - (DATE_TIMELINE_VISIBLE_DAYS - 1));
      setStartDate(initialStart);

      setSelectedDate(calculatedDay)
      console.log(selectedDateIndex)
      console.log(todayIndex)
      const index = allDates.findIndex(date => {
        const dateObj = dayjs(date);
        return (
          dateObj.date() === calculatedDay.date() &&
          dateObj.month() === calculatedDay.month() &&
          dateObj.year() === calculatedDay.year()
        );
      })
      console.log(index)
      setSelectedDateIndex(index)
    }
  }, [calculatedDay])

  useEffect(() => {
    switch (camerawithoutGroups.length) {
      case 1:
        setXSCountVal(12);
        break;
      case 2:
        setXSCountVal(6);
        break;
      case 3:
        setXSCountVal(4);
        break;
      default:
        setXSCountVal(3);
        break;
    }
  
    setOuterBoxWidth('100%');
    setCamewidth('100%');
  }, [camerawithoutGroups]);
    

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientNow = await getClientNow();
        setCalculatedDay(clientNow);
      } catch (error) {
        console.error("Error calculating time:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_VDO === "LOCAL") {
      setLocalURL(true);
      // console.log("string.. ")
    }
  }, []);



  const getCamerasList = (
    selectedProperty = property,
    requestId = propertyRequestRef.current
  ) =>{
    let bodyParams = {};
    if(userType === "2") {
      if (!['share', 'sub', 'merge'].includes(Type)) {
        bodyParams.status = '0,1';
        if (selectedProperty !== null) {
          bodyParams.property_id = Number(selectedProperty);
        } else {
          bodyParams.clientid = Number(client_id);
        } 
      }
    }


    bodyParams = jsonToParam(bodyParams);

    getClientCameras(bodyParams, (response) => {
      if (requestId !== propertyRequestRef.current) return;

      if (response.status === 200) {
        if (response.data !== null) {
          const propertyCameras =
            userType === "2" && selectedProperty !== null
              ? response.data.filter(
                  (camera) =>
                    Number(camera?.clientid ?? camera?.client_id ?? camera?.ClientId ?? 0) ===
                    Number(selectedProperty)
                )
              : response.data;

          setAllCameraListWithoutGroups(propertyCameras);
        } else {
          setAllCameraListWithoutGroups([]);
        }
      }
    });
  }

  useEffect(() => {
    getFilteredCameras();
  }, [allCamerawithoutGroups, location]);

  useEffect(()=>{
    const requestId = ++propertyRequestRef.current;
    getCamerasList(property, requestId);
  },[property])

  const getFilteredCameras = () => {
    let camerasList = allCamerawithoutGroups;
    if (client_id === "1316") {
      camerasList = camerasList.filter(
        (camera) => Number(camera.LocationId) === Number(location)
      );
    }

    setCameraListWithoutGroups(camerasList);
  }

  const changeProperty = (value) => {
    propertyRequestRef.current += 1;
    setAllCameraListWithoutGroups([]);
    setCameraListWithoutGroups([]);
    setClientGroupsList([]);
    setCamerasByGroup([]);
    setActiveGroups([]);
    setActiveCameras([]);
    setInputValue([]);
    setCheckValue(false);
    setGroupCheckValue(false);
    setProperty(value);
  };

  useEffect(() => {

    
    if(AssignClient!=='' && storedProperty!=='') {
      const assignedLocations = getAssignedLocationsFromStorage();
      setProperties(assignedLocations);

      const stored = localStorage.getItem('selectedProperty');
      if (stored) {
        const found = assignedLocations.find((loc) => loc.ID === parseInt(stored));
        if (found) {
          setProperty(found.ID);
        }
      } else {
        const first = assignedLocations[0];
        if (first) {
          setProperty(first.ID);
          localStorage.setItem('selectedProperty', first.ID);
        }
      }
    }

    const LocationBody = { client_id: Number(client_id) }
    getClientLocations(LocationBody, (response) => {

      if (response.status === 200) {

        setLocations(response.data);

        const stored = localStorage.getItem('selectedLocation');
        if (stored) {
          const found = response.data.find((loc) => loc.Id === parseInt(stored));
          if (found) {
            setLocation(found.Id);
          }
        } else {
          const first = response.data[0];
          if (first) {
            setLocation(first.Id);
            localStorage.setItem('selectedLocation', first.Id);
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    let groupId = 0;
    let body = { clientId: client_id, groupId: groupId };
    getClientGroupInfo(body, (response) => {
      if (response.status === 200) {
        // console.log("response Data group ", response.data);
        setGroupinfo(response.data);
        const uniqueGroups = Array.from(
          new Map(
            response.data.map((item) => [
              item.GroupId,
              { GroupId: item.GroupId, GroupName: item.GroupName },
            ])
          ).values()
        );
        setClientGroupsList(uniqueGroups);
      }
    });
  }, []);

  useEffect(() => {
    const video = document.getElementById("videoPlayer");
    if (video) {
      video.playbackRate = selectedSpeed;
    }
  }, []);

  useEffect(() => {
    if (
      activeGroups.length === clientGroupList.length &&
      activeGroups.length > 0
    ) {
      setGroupCheckValue(true);
    }

    if (activeGroups.length > 0) {
      let cameras = groupinfo.filter((val) =>
        activeGroups.includes(val.GroupId)
      );
      setCamerasByGroup(cameras);
      const newId = cameras.map((val) => val.deviceid);
      const filArr = activeCameras.filter((val) => newId.includes(val));
      // console.log(filArr)
      setActiveCameras(filArr);

      if (cameras.length === filArr.length && filArr.length > 0) {
        setCheckValue(true);
      }
    }
  }, [activeGroups]);

  //const getPlaybackUrl = (item) => item?.play_url || item?.VideoUrl || "";
  const getPlaybackUrl = (item) => item?.play_url || "";

  const handleSubmit = (id) => {

    setIsTabledata(false);
    let startTime = '', endTime = '';
    if (id) {
      console.log(selectedDateRange)
      if (!selectedDateRange) {
        startTime = calculatedDay.format('YYYY-MM-DD 00:00:00')
        endTime = calculatedDay.format('YYYY-MM-DD 23:59:59')
      }
    }
    const data = {
      camera_id: id,
      start_time: selectedDateRange.fromDate,
      end_time: selectedDateRange.toDate,
    };

    getClientEscalatedVideos(data, (response) => {
      if (response.status === 200 && response.data !== null) {
        const rows = Array.isArray(response.data) ? response.data : [];
        setEscalatedVideos(rows);
        setEscalatedSummary(rows);
        setCurrentDeviceIndex(0);
        console.log('[escalatedSummary] loaded', rows.length, 'clips');
        rows.forEach((item, i) => console.log(`  [${i}] local_play_url=${item?.local_play_url} | play_url=${item?.play_url} | VideoUrl=${item?.VideoUrl}`));
        setIsTabledata(rows.length > 0);
        setLoadingSubmit(false);
        setLoading(false);
        setIsPlaying(true);
      } else {
        setEscalatedVideos([]);
        setEscalatedSummary([]);
        setCurrentDeviceIndex(0);
        setIsTabledata(false);
        setLoading(false);
      }
    });
  };

  // Fetch a single time-window of recordings for the active camera. `deviceId` is
  // passed explicitly when opening the player (state isn't set yet at that point).
  const fetchWindow = (startTime, endTime, cb, deviceId = currentCameraId) => {
    const fmt = (d) => d.format('YYYY-MM-DD HH:mm:ss');
    getClientEscalatedVideos(
      { camera_id: deviceId, start_time: fmt(startTime), end_time: fmt(endTime) },
      (response) => {
        const clips = response.status === 200 && Array.isArray(response.data) ? response.data : [];
        cb(clips);
      }
    );
  };

  const handlePlayerDateChange = (newDate) => {
    if (!newDate || !currentCameraId) return;
    setLoading(true);

    // Debounce rapid selections (dragging/clicking around the timeline) so only the
    // last one actually fetches — instead of firing a slow request per position.
    if (dateChangeTimerRef.current) clearTimeout(dateChangeTimerRef.current);
    dateChangeTimerRef.current = setTimeout(() => {
      const seq = ++fetchSeqRef.current; // latest-wins: ignore any earlier in-flight fetch

      const windowStart = newDate.subtract(WINDOW_BEFORE_MIN, 'minute');
      const dayEnd = newDate.endOf('day');
      let windowEnd = newDate.add(WINDOW_AFTER_MIN, 'minute');
      if (windowEnd.isAfter(dayEnd)) windowEnd = dayEnd;

      fetchWindow(windowStart, windowEnd, (clips) => {
        if (seq !== fetchSeqRef.current) return; // superseded by a newer selection
        // No footage at the requested time — keep the current playback instead of
        // blanking the player into a dead-end (click-to-load or picker on a gap).
        if (!Array.isArray(clips) || clips.length === 0) {
          setLoading(false);
          return;
        }
        // Reset paging state for the new window.
        loadedKeysRef.current = new Set(clips.map(clipKey).filter(Boolean));
        loadedWindowEndRef.current = windowEnd;
        hasMoreRef.current = windowEnd.isBefore(dayEnd);
        prefetchingRef.current = false;
        pendingAutoAdvanceRef.current = false;

        // Batch so the clips array + index apply in one render. Without this
        // (React 17 doesn't batch async callbacks) the new clips land a render
        // before the new index, so escalatedSummary[index] is momentarily
        // undefined — the player unmounts/remounts and the timeline jumps.
        unstable_batchedUpdates(() => {
          setEscalatedVideos(clips);
          setEscalatedSummary(clips);
          setCurrentDeviceIndex(findClipIndexForTime(clips, newDate));
          setSeekTargetTime(newDate);
          setPlayerDay(newDate.startOf('day'));
          setLoadedRange({ start: windowStart, end: windowEnd });
          setIsTabledata(true);
          setLoading(false);
        });
      });
    }, 180);
  };

  // Load the hour that follows the currently-loaded window and append its clips.
  // `triggerAdvance` = playback reached the end and should jump to the next clip
  // as soon as it arrives (vs. a silent background prefetch).
  const loadNextWindow = (triggerAdvance) => {
    if (triggerAdvance) pendingAutoAdvanceRef.current = true;
    if (prefetchingRef.current) return; // in flight — its completion will handle advance
    if (!hasMoreRef.current || !loadedWindowEndRef.current || !currentCameraId) {
      if (pendingAutoAdvanceRef.current) {
        pendingAutoAdvanceRef.current = false;
        handleClose();
      }
      return;
    }

    prefetchingRef.current = true;
    if (pendingAutoAdvanceRef.current) setLoading(true);
    // Tie this prefetch to the current session; if the user jumps elsewhere while
    // it's in flight, its result is ignored rather than appended to the wrong window.
    const seq = fetchSeqRef.current;

    const finish = () => {
      prefetchingRef.current = false;
      setLoading(false);
    };

    const attempt = (winStart) => {
      const dayEnd = winStart.endOf('day');
      let winEnd = winStart.add(NEXT_WINDOW_HOURS, 'hour');
      if (winEnd.isAfter(dayEnd)) winEnd = dayEnd;

      fetchWindow(winStart, winEnd, (clips) => {
        if (seq !== fetchSeqRef.current) { prefetchingRef.current = false; return; }
        const fresh = clips.filter((c) => {
          const k = clipKey(c);
          return k && !loadedKeysRef.current.has(k);
        });
        loadedWindowEndRef.current = winEnd;
        hasMoreRef.current = winEnd.isBefore(dayEnd);
        // The scanned/loaded span now reaches winEnd — shrink the "not loaded" area.
        setLoadedRange((prev) => (prev ? { ...prev, end: winEnd } : { start: winStart, end: winEnd }));

        if (fresh.length === 0) {
          // Empty hour (recording gap) — keep scanning forward until data or day end.
          if (hasMoreRef.current) {
            attempt(winEnd);
            return;
          }
          finish();
          if (pendingAutoAdvanceRef.current) {
            pendingAutoAdvanceRef.current = false;
            handleClose();
          }
          return;
        }

        fresh.forEach((c) => loadedKeysRef.current.add(clipKey(c)));
        const baseLen = deviceVideosRef.current.length;
        unstable_batchedUpdates(() => {
          setEscalatedSummary((prev) => [...prev, ...fresh]);
          setEscalatedVideos((prev) => [...prev, ...fresh]);
          if (pendingAutoAdvanceRef.current) {
            pendingAutoAdvanceRef.current = false;
            setSeekTargetTime(null);
            setCurrentDeviceIndex(baseLen); // first newly-appended clip
          }
          finish();
        });
      });
    };

    attempt(loadedWindowEndRef.current);
  };

  const handlePlaylistEnd = () => {
    loadNextWindow(true);
  };

  const handlePlayImage = (item) => {
    // console.log("image details ",item);
    setImageURL(item);
    setOpenDialogImage(true);
  };

  const handlePlayVideo = (deviceID) => {
    setCurrentCameraId(deviceID);
    setOpenDialog(true);
    setLoading(true);
    setIsTabledata(false);
    setEmail("");
    setErrorEmail("");

    // Anchor the player to the selected calendar day and load just its first hour
    // (00:00 → 01:00), lazy-loading the rest as playback advances — instead of
    // pulling the whole 24h. If that first hour is empty, scan forward hour by hour
    // to the day's earliest footage so the player doesn't open on a dead stretch.
    const dayStart = selectedDateRange?.fromDate
      ? dayjs(selectedDateRange.fromDate).startOf('day')
      : (calculatedDay ? dayjs(calculatedDay) : dayjs()).startOf('day');
    const dayEnd = dayStart.endOf('day');

    setPlayerDay(dayStart);
    setEscalatedSummary([]);
    setEscalatedVideos([]);
    setCurrentDeviceIndex(0);

    // New session — invalidate any in-flight/queued window fetch.
    if (dateChangeTimerRef.current) { clearTimeout(dateChangeTimerRef.current); dateChangeTimerRef.current = null; }
    const seq = ++fetchSeqRef.current;

    const scan = (winStart) => {
      let winEnd = winStart.add(NEXT_WINDOW_HOURS, 'hour');
      if (winEnd.isAfter(dayEnd)) winEnd = dayEnd;

      fetchWindow(winStart, winEnd, (clips) => {
        if (seq !== fetchSeqRef.current) return; // superseded by a newer selection
        // Empty hour and the day isn't over — skip ahead to the next hour so we
        // land on real footage (keeps the earlier, unchecked span shown as
        // "not loaded" gray rather than red "not available").
        if (clips.length === 0 && winEnd.isBefore(dayEnd)) {
          scan(winEnd);
          return;
        }
        loadedKeysRef.current = new Set(clips.map(clipKey).filter(Boolean));
        loadedWindowEndRef.current = winEnd;
        hasMoreRef.current = winEnd.isBefore(dayEnd);
        prefetchingRef.current = false;
        pendingAutoAdvanceRef.current = false;

        // Batch (React 17 doesn't batch async callbacks) so clips + index land together.
        unstable_batchedUpdates(() => {
          setEscalatedVideos(clips);
          setEscalatedSummary(clips);
          setCurrentDeviceIndex(findClipIndexForTime(clips, winStart));
          setSeekTargetTime(winStart);
          setLoadedRange({ start: winStart, end: winEnd });
          setIsTabledata(clips.length > 0);
          setLoading(false);
        });
      }, deviceID);
    };

    scan(dayStart);
  };

  useEffect(() => {
    const video = document.getElementById("videoPlayer");
    if (video) {
      video.playbackRate = selectedSpeed;
    }
  }, [selectedSpeed, currentDeviceIndex]);

  // Keep a synchronous mirror of the loaded clip list for loadNextWindow's append.
  useEffect(() => {
    deviceVideosRef.current = escalatedSummary;
  }, [escalatedSummary]);

  // Proactively prefetch the next hour once playback reaches the last loaded clip.
  useEffect(() => {
    if (!openDialog || !hasMoreRef.current || prefetchingRef.current) return;
    if (escalatedSummary.length > 0 && currentDeviceIndex >= escalatedSummary.length - 1) {
      loadNextWindow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeviceIndex, escalatedSummary.length, openDialog]);

  const handleClose = (event) => {
    setOpenDialog(false);
    setOpenDialogImage(false);
    setSelectedSpeed(3);
    setEmail("");
    setErrorEmail("");
    setPlaybackRate(1);
    // Invalidate any in-flight/queued window fetch so a late response can't reopen
    // or repopulate the player after it's been closed.
    if (dateChangeTimerRef.current) { clearTimeout(dateChangeTimerRef.current); dateChangeTimerRef.current = null; }
    fetchSeqRef.current += 1;
    prefetchingRef.current = false;
    pendingAutoAdvanceRef.current = false;
  };

  const handleDownload = (videoUrl) => async () => {
    setDownloading(true)
    console.log("videoUrl", videoUrl);
    if (!videoUrl) return;

    try {
      const filename = videoUrl.split("/").pop();
      const response = await fetch(videoUrl, { mode: 'cors' }); // CORS mode
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || `video_${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Free memory
      window.URL.revokeObjectURL(blobUrl);
      setDownloading(false)
      setDownloadText(true);

      setTimeout(() => {
        setDownloadText(false);
      }, 2000);

    } catch (error) {
      console.error("Download error:", error);
      alert("Unable to download video.");
    }
  };




  useEffect(() => {
    setSelectedSpeed(3); // Whenever currentDeviceIndex changes, set speed to 3x
  }, [currentDeviceIndex]);





  const createRailBackground = () => {

    if (!timeSlots || timeSlots.size === 0) {
      return "linear-gradient(to right, rgba(30, 144, 255, 0.4) 0% 100%)";
    }

    const totalMinutes = 1440;
    const segments = [];
    let currentColor = null;
    let segmentStart = 0;


    // const filteredTimes = Array.from(timeSlots)
    // .filter(minutes => minutes < 1380) 
    // .sort((a, b) => a - b);  

    const filteredTimes = Array.from(timeSlots).pop();

    // const removedTimes = Array.from(timeSlots)
    // .filter(minutes => minutes >= 1380);

    // const lastBlueMinute = filteredTimes.length > 0 
    //   ? Math.max(...filteredTimes) 
    //   : totalMinutes;

    const lastBlueMinute = filteredTimes

    for (let minute = 0; minute <= totalMinutes; minute++) {
      //const isEvent = timeSlots.has(minute);

      const isEvent = [...eventSlots].some(
        eventMinute => Math.abs(eventMinute - minute) <= 1
      );

      const isArchive = [...timeSlots].some(
        eventMinute => Math.abs(eventMinute - minute) <= 1
      );

      let color = isEvent
        ? "rgba(228, 56, 57, 0.8)"
        : "rgba(30, 144, 255, 0.4)";

      if ((minute >= lastBlueMinute && !isEvent)) {
        color = 'rgba(148, 153, 159, 0.6)';
        // if(removedTimes.includes(minute)){

        // }
        // else{

        // }

        //    const isNearRemovedTime = removedTimes.some(removedMinute => 
        //   Math.abs(removedMinute - minute) <= 5
        // );

        // color = isNearRemovedTime 
        //   ? 'rgba(30, 144, 255, 0.4)' 
        //   : 'rgba(148, 153, 159, 0.6)';
      }

      if (color !== currentColor || minute === totalMinutes) {
        if (currentColor !== null) {
          const percentageStart = (segmentStart / totalMinutes) * 100;
          const percentageEnd = (minute / totalMinutes) * 100;
          segments.push(
            `${currentColor} ${percentageStart}% ${percentageEnd}%`
          );
        }
        currentColor = color;
        segmentStart = minute;
      }
    }
    //console.log('segments', segments)
    return `linear-gradient(to right, ${segments.join(", ")})`;
  };


  //   const handleSliderChange = (event, newValue) => {
  //     const hours = Math.floor(newValue / 60);
  //     const minutes = newValue % 60;
  //     const selectedTime = dayjs(selectedDateRange.fromDate)
  //       .hour(hours)
  //       .minute(minutes)
  //       .second(0);
  //     const fromDatefor = selectedTime;
  //     const isValidDate = (value) => {
  //       const date = new Date(value);
  //       return !isNaN(date.getTime());
  //     };
  //     const parseDate = (dateString) => {
  //       if (!isValidDate(dateString)) {
  //         console.error("Invalid date:", dateString);
  //         return null;
  //       }
  //       return new Date(dateString);
  //     };
  //     const formatDateTime = (date) => {
  //       const year = date.getFullYear();
  //       const month = String(date.getMonth() + 1).padStart(2, "0");
  //       const day = String(date.getDate()).padStart(2, "0");
  //       const hours = String(date.getHours()).padStart(2, "0");
  //       const minutes = String(date.getMinutes()).padStart(2, "0");
  //       const seconds = String(date.getSeconds()).padStart(2, "0");
  //       return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  //     };
  //     const fromDateObj = parseDate(fromDatefor);
  //     const formattedFromDate = formatDateTime(fromDateObj);
  //     console.log("Formatted Selected Time:", formattedFromDate);
  //     // Find the closest match within 1 minute
  // // Find the nearest match based on minimal time difference
  //       let closestIndex = -1;
  //       let smallestDiff = Infinity;

  //       escalatedSummary.forEach((summary, index) => {
  //         const createdOnDate = parseDate(summary.CreatedOn);
  //         if (!createdOnDate) return;
  //         const diff = Math.abs(fromDateObj - createdOnDate);
  //         if (diff < smallestDiff) {
  //           smallestDiff = diff;
  //           closestIndex = index;
  //         }
  //       });

  //       if (closestIndex !== -1) {
  //         console.log("Nearest match found at index:", closestIndex);
  //         setCurrentDeviceIndex(closestIndex);
  //       } else {
  //         console.warn("No video entries to match.");
  //       }

  //       const video = videoRef.current;
  //       if (video) {
  //         video.playbackRate = playbackRate;
  //       }

  //   };

  const getNearestValidTime = (targetValue) => {

    const allValidTimes = Array.from(new Set([...timeSlots, ...eventSlots]));

    if (allValidTimes.length === 0) return targetValue;


    const closest = allValidTimes.reduce((prev, curr) =>
      Math.abs(curr - targetValue) < Math.abs(prev - targetValue) ? curr : prev
    );

    return closest;
  };

  const handleSliderChange = (event, newValue) => {

    const nearestValidValue = getNearestValidTime(newValue);
    setValue(nearestValidValue);
    // Slider value (minutes) -> time-of-day (HH:mm:ss)
    const hours = Math.floor(newValue / 60);
    const minutes = newValue % 60;
    const selectedSeconds = hours * 3600 + minutes * 60; // time-of-day only

    const isValidDate = (value) => {
      const date = new Date(value);
      return !isNaN(date.getTime());
    };

    const parseDate = (dateString) => {
      if (!isValidDate(dateString)) {
        console.error("Invalid date:", dateString);
        return null;
      }
      return new Date(dateString);
    };

    let closestIndex = -1;
    let smallestDiff = Infinity;
    const SECONDS_IN_DAY = 24 * 60 * 60;
    const MATCH_WINDOW_SECONDS = null;

    escalatedSummary.forEach((summary, index) => {
      const createdOnDate = parseDate(summary.CreatedOn);
      if (!createdOnDate) return;

      const createdSeconds =
        createdOnDate.getHours() * 3600 +
        createdOnDate.getMinutes() * 60 +
        createdOnDate.getSeconds();


      let diff = Math.abs(selectedSeconds - createdSeconds);
      diff = Math.min(diff, SECONDS_IN_DAY - diff);

      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIndex = index;
      }
    });

    if (
      closestIndex !== -1 &&
      (MATCH_WINDOW_SECONDS == null || smallestDiff <= MATCH_WINDOW_SECONDS)
    ) {
      console.log("Nearest time-of-day match at index:", closestIndex);
      setCurrentDeviceIndex(closestIndex);
    } else {
      console.warn("No video entries to match (time-of-day).");
    }

    // Keep your playback rate behavior
    const video = videoRef.current;
    if (video) {
      video.playbackRate = playbackRate;
    }
  };


  const increaseSpeed = () => {
    if (isInSlow) {
      const currentIndex = slowMotionSpeeds.indexOf(playbackRate);
      if (currentIndex < slowMotionSpeeds.length - 1) {
        // Go to next slow speed
        const newRate = slowMotionSpeeds[currentIndex + 1];
        setPlaybackRate(newRate);
        if (videoRef.current) videoRef.current.playbackRate = newRate;
      } else {
        // Jump to first fast speed (1.0)
        const newRate = fastForwardSpeeds[0];
        setPlaybackRate(newRate);
        if (videoRef.current) videoRef.current.playbackRate = newRate;
      }
    } else if (isInFast) {
      const currentIndex = fastForwardSpeeds.indexOf(playbackRate);
      if (currentIndex < fastForwardSpeeds.length - 1) {
        const newRate = fastForwardSpeeds[currentIndex + 1];
        setPlaybackRate(newRate);
        if (videoRef.current) videoRef.current.playbackRate = newRate;
      }
    }
  };

  const decreaseSpeed = () => {
    if (isInFast) {
      if (playbackRate > fastForwardSpeeds[0]) {
        const currentIndex = fastForwardSpeeds.indexOf(playbackRate);
        const newRate = fastForwardSpeeds[currentIndex - 1];
        setPlaybackRate(newRate);
        if (videoRef.current) videoRef.current.playbackRate = newRate;
      } else {
        const newRate = slowMotionSpeeds[slowMotionSpeeds.length - 1]; // start at 0.75
        setPlaybackRate(newRate);
        if (videoRef.current) videoRef.current.playbackRate = newRate;
      }
    } else if (isInSlow) {
      const currentIndex = slowMotionSpeeds.indexOf(playbackRate);
      if (currentIndex > 0) {
        const newRate = slowMotionSpeeds[currentIndex - 1];
        setPlaybackRate(newRate);
        if (videoRef.current) videoRef.current.playbackRate = newRate;
      }
    }
  };

  //Dates blocks

  // const getAllDates = () => {
  //   const allDates = [];
  //   const earliestDate = new Date(today);
  //   earliestDate.setDate(today.getDate() - DATE_TIMELINE_OFFSET);
  //   for (let i = 0; i < DATE_TIMELINE_DAYS; i++) {
  //     const newDate = new Date(earliestDate);
  //     newDate.setDate(earliestDate.getDate() + i);
  //     allDates.push(newDate);
  //   }
  //   return allDates;
  // };
  const allDates = useMemo(() => {
    if (!calculatedDay) return [];
    const calculatedDate = calculatedDay.toDate();
    const result = [];
    const earliestDate = new Date(calculatedDate);
    earliestDate.setDate(calculatedDate.getDate() - DATE_TIMELINE_OFFSET);
    for (let i = 0; i < DATE_TIMELINE_DAYS; i++) {
      const newDate = new Date(earliestDate);
      newDate.setDate(earliestDate.getDate() + i);
      result.push(newDate);
    }
    return result;
  }, [calculatedDay]);

  const todayIndex = useMemo(() => {
    if (!calculatedDay || allDates.length === 0) return -1;
    return allDates.findIndex(date => {
      const d = dayjs(date);
      return d.date() === calculatedDay.date() &&
        d.month() === calculatedDay.month() &&
        d.year() === calculatedDay.year();
    });
  }, [calculatedDay, allDates]);
  const [selectedDateIndex, setSelectedDateIndex] = useState(todayIndex); // Set to today's index



  const formatDate = (date) => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return `${days[date.getDay()]}, ${date.getDate()} ${date.toLocaleString(
      "default",
      { month: "short" }
    )}`;
  };

  const formatSelectedDateRange = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const fromDate = `${year}-${month}-${day} 00:00:00`;
    const toDate = `${year}-${month}-${day} 23:59:59`;
    return { fromDate, toDate };
  };

  // const getDates = (start) => {
  //   const dates = [];
  //   const maxDays = DATE_TIMELINE_DAYS;
  //   // const maxDays = start.getDate() === today.getDate() - 7 ? 8 : 7; // 8 for initial, 7 for prev
  //   for (let i = 0; i < maxDays; i++) {
  //     const newDate = new Date(start);
  //     newDate.setDate(start.getDate() + i);
  //     if (newDate <= today) {
  //       dates.push(newDate);
  //     } else {
  //       break;
  //     }
  //   }
  //   return dates;
  // };
  const dates = useMemo(() => {
    if (!calculatedDay || !startDate) return [];
    const latestDate = calculatedDay.toDate();
    const result = [];

    for (let i = 0; i < DATE_TIMELINE_VISIBLE_DAYS; i++) {
      const newDate = new Date(startDate);
      newDate.setDate(startDate.getDate() + i);

      if (newDate <= latestDate) {
        result.push(newDate);
      }
    }

    return result;
  }, [calculatedDay, startDate]);

  const handlePrev = () => {
    const newStart = new Date(startDate);
    newStart.setDate(startDate.getDate() - DATE_TIMELINE_VISIBLE_DAYS);
    setStartDate(newStart);
  };

  const handleNext = () => {
    const newStart = new Date(startDate);
    newStart.setDate(startDate.getDate() + DATE_TIMELINE_VISIBLE_DAYS);
    setStartDate(newStart);
  };

  const handleDateClick = (index) => {

    const clickedDate = dates[index];
    setSelectedDate(dayjs(clickedDate));
    const newIndex = allDates.findIndex(
      (date) =>
        date.getDate() === clickedDate.getDate() &&
        date.getMonth() === clickedDate.getMonth() &&
        date.getFullYear() === clickedDate.getFullYear()
    );
    setSelectedDateIndex(newIndex);
  };

  const isNextDisabled = () => {
    if (!calculatedDay || dates.length === 0) return true;
    const lastDisplayedDate = dates[dates.length - 1];
    const latestDate = calculatedDay.toDate();
    return (
      lastDisplayedDate.getDate() === latestDate.getDate() &&
      lastDisplayedDate.getMonth() === latestDate.getMonth() &&
      lastDisplayedDate.getFullYear() === latestDate.getFullYear()
    );
  };

  const isPrevDisabled = () => {
    if (allDates.length === 0 || dates.length === 0) return true;
    const firstDisplayedDate = dates[0];
    const earliestDate = allDates[0];
    return (
      firstDisplayedDate.getDate() === earliestDate.getDate() &&
      firstDisplayedDate.getMonth() === earliestDate.getMonth() &&
      firstDisplayedDate.getFullYear() === earliestDate.getFullYear()
    );
  };



  const selectedDateRange = useMemo(() =>
    selectedDateIndex !== -1 && allDates[selectedDateIndex]
      ? formatSelectedDateRange(allDates[selectedDateIndex])
      : null,
  [selectedDateIndex, allDates]);



  //   useEffect(() => {
  //   const video = videoRef.current;
  //   if (!video) return;

  //   const onPlay = () => setIsPlaying(true);
  //   const onPause = () => setIsPlaying(false);

  //   video.addEventListener('play', onPlay);
  //   video.addEventListener('pause', onPause);

  //   return () => {
  //     video.removeEventListener('play', onPlay);
  //     video.removeEventListener('pause', onPause);
  //   };
  // }, [videoRef.current]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  //   useEffect(() => {
  //   const video = videoRef.current;
  //   if (!video) return;

  //   const onPlay = () => setIsPlaying(true);
  //   const onPause = () => setIsPlaying(false);

  //   video.addEventListener('play', onPlay);
  //   video.addEventListener('pause', onPause);

  //   return () => {
  //     video.removeEventListener('play', onPlay);
  //     video.removeEventListener('pause', onPause);
  //   };
  // }, [escalatedVideos[currentDeviceIndex]?.play_url]);

  //slider

  useEffect(() => {
    if (openDialog) {
      setValue(0); // Reset the slider to the start
      setCurrentDeviceIndex(0); // Optional: reset the video to the first
    }
  }, [openDialog]);

  useEffect(() => {
    // Track the currently played clip as watched
    if (currentDeviceIndex >= 0) {
      setWatchedClips((prev) => new Set([...prev, currentDeviceIndex]));
    }
    const item = escalatedSummary[currentDeviceIndex];
    const resolvedUrl = item?.local_play_url || item?.play_url || item?.VideoUrl || '(none)';
    console.log('[currentDeviceIndex]', currentDeviceIndex, '| url:', resolvedUrl);
  }, [currentDeviceIndex]);


  const createVideoProgressGradient = () => {
    const total = escalatedVideos.length;
    const segments = [];

    for (let i = 0; i < total; i++) {
      let color = "lightgray"; // Upcoming
      if (i < currentDeviceIndex) {
        color = "green"; // Watched
      } else if (i === currentDeviceIndex) {
        color = "#1976d2"; // Current playing
      }

      const start = (i / total) * 100;
      const end = ((i + 1) / total) * 100;
      segments.push(`${color} ${start}% ${end}%`);
    }

    return `linear-gradient(to right, ${segments.join(", ")})`;
  };


  return (
    <Page title="Playback">
      <Container maxWidth="xxl">

        {/* ── Header ── */}
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4">Playback</Typography>
          </Box>
          <Breadcrumbs separator="•" aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/" sx={{ fontSize: '0.875rem' }}>
              Dashboard
            </Link>
            <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>
              Playback
            </Typography>
          </Breadcrumbs>
        </Box>

        <Card variant="outlined">
          <CardContent>

            {/* ── Date & Location Selector ── */}
            <DatePickerWrapper>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, width: '100%' }}>
                {properties.length > 1 && (
                  <FormControl sx={{ minWidth: 240 }}>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={property || ''}
                      label="Location"
                      onChange={(e) => {
                        const val = e.target.value;
                        localStorage.setItem('selectedProperty', val);
                        changeProperty(val);
                      }}
                    >
                      {properties.map((event) => (
                        <MenuItem key={event.ID} value={event.ID}>
                          {event.Location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {client_id === "1316" && locations.length > 1 && (
                  <FormControl sx={{ minWidth: 240 }}>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={location || ''}
                      label="Location"
                      onChange={(e) => {
                        const val = e.target.value;
                        setLocation(val);
                        localStorage.setItem('selectedLocation', val);
                      }}
                    >
                      {locations.map((event) => (
                        <MenuItem key={event.Id} value={event.Id}>
                          {event.LocationName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <NavButtonContainer>
                  <NavButton onClick={handlePrev} disabled={isPrevDisabled()}>
                    {'<'}
                  </NavButton>

                  <DateRow>
                    {dates.map((date, index) => {
                      const globalIndex = allDates.findIndex(
                        (d) =>
                          d.getDate() === date.getDate() &&
                          d.getMonth() === date.getMonth() &&
                          d.getFullYear() === date.getFullYear()
                      );
                      const parsedDate = new Date(date);
                      const month = parsedDate.toLocaleString('en-US', { month: 'short' });
                      const dayNumber = parsedDate.getDate();
                      const dayName = parsedDate.toLocaleString('en-US', { weekday: 'short' });
                      return (
                        <DateBox
                          key={index}
                          isSelected={globalIndex === selectedDateIndex}
                          onClick={() => handleDateClick(index)}
                        >
                          <DateText component="div">
                            <MonthText>{month}</MonthText>
                            <DayNumber>{dayNumber}</DayNumber>
                            <DayText>{dayName}</DayText>
                          </DateText>
                        </DateBox>
                      );
                    })}
                  </DateRow>

                  <NavButton onClick={handleNext} disabled={isNextDisabled()}>
                    {'>'}
                  </NavButton>
                </NavButtonContainer>
              </Box>
            </DatePickerWrapper>

            <Divider sx={{ my: 2 }} />

            {/* ── Camera Grid ── */}
            <Grid container spacing={2}>
              {camerawithoutGroups.length > 0 ? (
                camerawithoutGroups.map((item, index) => (
                  <Grid item xs={12} sm={xsCount} key={index}>
                    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ py: 1, bgcolor: 'background.neutral', borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {item.devicename}
                        </Typography>
                      </CardContent>
                      <Box
                        sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: 300, cursor: 'pointer' }}
                        onClick={() => handlePlayVideo(item.deviceid)}
                      >
                        {item.snapshot && (
                          <CardMedia
                            component="img"
                            image={item.snapshot}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <Avatar
                          sx={{
                            position: 'absolute',
                            zIndex: 2,
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          <Iconify icon="eva:play-circle-outline" width={30} height={30} />
                        </Avatar>
                      </Box>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body1" align="center" color="text.secondary" sx={{ py: 4 }}>
                    {isTabledata ? 'No Data' : ''}
                  </Typography>
                </Grid>
              )}
            </Grid>

          <Dialog open={openDialog} fullScreen>
            {loading ? (
              <Box sx={{ p: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : isTabledata && escalatedSummary.length > 0 ? (
              <PlaybackVideoPlayer
                embedded
                sourceUrl={getPlaybackUrl(escalatedSummary[currentDeviceIndex])}
                currentClip={escalatedSummary[currentDeviceIndex]}
                timelineData={escalatedSummary}
                cameraLabel={escalatedVideos?.[0]?.devicename || 'Playback'}
                cameraId={currentCameraId}
                onPrevChunk={() => setCurrentDeviceIndex((prev) => Math.max(0, prev - 1))}
                onNextChunk={() => setCurrentDeviceIndex((prev) => Math.min(escalatedSummary.length - 1, prev + 1))}
                hasPrevChunk={currentDeviceIndex > 0}
                hasNextChunk={currentDeviceIndex < escalatedSummary.length - 1}
                onClose={handleClose}
                onDateChange={handlePlayerDateChange}
                onPlaylistEnd={handlePlaylistEnd}
                seekTarget={seekTargetTime}
                timelineDay={playerDay}
                loadedRange={loadedRange}
                onSelectClip={(index) => { setSeekTargetTime(null); setCurrentDeviceIndex(index); }}
              />
            ) : (
              <Box sx={{ p: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Playback are not available for the selected date. Try another.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button color="error" variant="contained" onClick={handleClose}>
                    Close
                  </Button>
                </Box>
              </Box>
            )}
          </Dialog>
        </CardContent>
      </Card>
      </Container>
    </Page>
  );

};

export default VideoArchives;
